/**
 * VRG-156 — Tag-shop catalog pricing + SKU/tag_family derivation.
 *
 * Model REVERT (VRG-156, supersedes the VERA-246 form×capability build): the shop
 * is back to **Product = form factor (7 products)**, with Capability as a selectable
 * variant axis climbing a per-form ladder. Sensor/UHF form removed; Card added;
 * Embroidered→Woven→(FabricPatch + FabricLabel), Inlay→Embed renamed.
 *
 * Canonical model lives in the umbrella repo:
 *   supply/tag-shop-catalog.md                       (spec)
 *   supply/scripts/shopify-create-tag-products.mjs   (the seed script — these
 *   constants MIRROR that script 1:1 so Shopify prices and the platform agree).
 *
 * ⚠️ PROVISIONAL NUMBERS. The pricing *formula* + min-markup-over-landed floor are
 * final; the input constants (FORM_BASE, CAP_DELTA, CHIP_COST, TARIFF_PCT, …) are
 * EXTRAPOLATED and pending real supplier RFQs + NXP quotes + a customs-broker
 * tariff check (see supply/tag-shop-catalog.md §Pricing "⚠️ do NOT skip"). Only the
 * constants change when quotes land; the math does not.
 */
import type { TagFamily } from '@repo/types'

// ─── Domain ──────────────────────────────────────────────────────────────────

export type TagCapability =
  | 'Connect'
  | 'Verify'
  | 'Verify + Tamper'
  | 'Verify + Trace'
  | 'Verify + Vault'
  | 'Temperature'
  | 'Moisture'
  | 'Pressure'
  | 'Proximity'

/** 8 form-factor products. Sensor/UHF removed; Card (new).
 *
 *  ⚠️ EMBED REMOVED 2026-09-22, on Spencer's call: "not sure what the embed tag is —
 *  remove it from the catalog for now." It was a complete data row — title,
 *  description, four sizes, four capabilities, ten combos and a price — and
 *  referenced by NOTHING. Evidence gathered before removing it:
 *
 *    references outside constants/tagCatalog/   zero
 *    entry in variantImagery.ts (product art)   none
 *    photograph anywhere                        none
 *    entered the catalogue                      963bb1bb, 2026-08-03
 *
 *  It had a price and SKUs only because the pricing functions generate those
 *  for every form mechanically, which is exactly how a product nobody had
 *  decided to sell ended up advertised on five industry pages of the marketing
 *  site. The count above was ALREADY WRONG when this said 8 — there were 9
 *  forms — so the removal has made a stale comment accidentally true. Corrected
 *  rather than left, because the next person to trust it would be right by luck.
 *
 *  ⚠️ TO RESTORE IT: this was a clean union removal across nine sites — the
 *  `TagFormFactor` union here, FORM_CODE, FORM_BASE, FORM_CONV, SIDES and
 *  FERRITE_FORMS in this file, FORM_INFO and ORDER in catalog.ts, and
 *  FORM_AFFINITY and FORM_META in affinity.ts. `git show` this commit for the
 *  exact values; do not retype them from memory, the prices were load-bearing.
 *
 *  `Hang` REMOVED 2026-09-25 (VRG-630) and REPLACED by `ClosureLoop` — the same
 *  nine sites, plus `PHOTOGRAPHED_SIZE`'s docblock and two spec cases. Unlike
 *  Embed this form was NOT unreferenced: it was removed because its two
 *  capabilities were each untrue. See the `ClosureLoop` member of the union
 *  below for the evidence, and VRG-630 for the eighteen product types it served
 *  and where each went.
 *
 *  ⚠️ THE EMBED REMOVAL LEFT AN ARTIFACT STALE FOR WEEKS. `98745a1` re-emitted
 *  `tag-catalog.generated.json` and not `TAGS.md`, so the generated reference
 *  kept describing nine forms with a live Embed section. There are TWO emitters
 *  (`scripts/emit-tag-catalog.ts`, `scripts/emit-tags-doc.ts`) and a form change
 *  invalidates both. Run both.
 *
 *  Woven SPLIT into FabricPatch + FabricLabel (2026-08-05). One product was holding
 *  two shape families: the size grid had already bifurcated into 16/25mm round-square
 *  (patch) and 45x25mm rectangular (label, forced by Trace's dual-freq dipole, which
 *  physically cannot fit a round coil). Renamed Woven -> Fabric because the old name
 *  was already known-inaccurate for the Extra-Durable grade -- a PPS capsule is not
 *  woven, which the catalog acknowledged by stretching "woven" to an umbrella term.
 *  "Fabric" is a material, not a use-case, so it still satisfies the construction-
 *  naming convention that ruled out "Garment". Durability is ORTHOGONAL to attachment:
 *  a durable patch is still sewn direct; a durable label is a hard tag encapsulated in
 *  a fabric carrier and sewn at the side seam -- the carrier folds, the capsule doesn't. */
export type TagFormFactor =
  | 'Sticker'
  | 'Solid'
  | 'FabricPatch'
  | 'FabricLabel'
  | 'Closure'
  /**
   * VRG-630 — the twin-conductor cord tamper tag. Coming soon; see
   * COMING_SOON_FORMS in capabilities.ts. It is the form `Hang` should have
   * been and was not.
   *
   * ⚠️ IT REPLACES `Hang`, AND THE REASON IS THAT A HANG TAG IS NOT A PRODUCT.
   * Checkpoint Systems describes an NFC hang tag as "an RFID inlay sandwiched
   * between two pieces of card"; SML sells its inlays "integrated inside or
   * attached to hangtags as a sticker". For Connect-only use a hang tag is a
   * printed card with one of our Stickers on it — so the Connect half of Hang's
   * ladder was a second name for a Sticker, not a second part.
   *
   * ⚠️ AND THE TAMPER HALF WAS A CLAIM NOTHING BACKED. `Hang` shipped
   * `Verify + Tamper` on the argument that "on THIS form the tamper loop IS the
   * attachment". No such part is sold anywhere: every loop-form NFC product
   * uses a non-tamper chip, and every tamper-chip product is a flat disc with a
   * tail. The catalogue was advertising a differentiator that does not exist.
   *
   * Closure Loop is the part that would make the claim true, and it is
   * FEASIBLE rather than aspirational: NXP caps a tamper loop at 2.5cm², which
   * a conductive ring violates outright, but two adjacent conductors held at
   * ≤1.67mm separation down a 150mm cord satisfy. It is not yet made, so it is
   * visible and NOT orderable — the same treatment `Temperature` already gets.
   */
  | 'ClosureLoop'
  | 'Card'
  /**
   * The watch-dial tag. Its own form since 2026-09-18, split out of Sticker.
   *
   * ⚠️ IT IS NOT A STICKER WITH AN OPTION TICKED. Ferrite is bonded in and
   * cannot be switched off — `docs/guides/tag-shop.md`: "it mounts behind a
   * dial, so on-metal construction is included and cannot be switched off". An
   * add-on you cannot remove is not an add-on; it is a different part, with a
   * different thickness, a different application and a different photograph.
   *
   * Two supporting signals it never fitted the Sticker ladder: its size was
   * appended OUT OF NUMERIC ORDER (…'38mm','45mm','28mm'), and expressing
   * "always on-metal" inside Sticker would have required forced-option
   * machinery that exists for no other size.
   *
   * ⚠️ STICKER STILL SELLS A 28mm SIZE and keeps it. Spencer, 2026-09-18: "we
   * still have a 28mm sticker tag, but it's not a dial — keep both." The two
   * are told apart by FORM, not size: VERA-STK-…-28-… vs VERA-DIAL-…-28-….
   */
  | 'Dial'

/**
 * The two fabric forms share every configurator behaviour -- durability grade,
 * embroidered-by-construction finish, and the XD SKU suffix. Callers must test this
 * rather than a single form name: when Woven split into Patch + Label, every
 * `form === 'Woven'` check silently became dead code that still type-checked.
 */
export const isFabricForm = (form: TagFormFactor): boolean =>
  form === 'FabricPatch' || form === 'FabricLabel'

export type QuantityTier = 100 | 250 | 500 | 1000

/**
 * chip family (tag_family) — DERIVED in VERA from the capability, NEVER stored in
 * Shopify and NEVER shown to the consumer (internal fulfillment detail only).
 * VRG-156: sense chips are now NXP phone-tap parts (NHS3152 / 22x DNA StatusDetect),
 * not Axzon UHF; Trace is EM4425.
 *
 * ⚠️ VERA-455 (2026-08-20): these are the `TagFamily` REGISTRY VALUES, not marketing part
 * names. The table used to hold display strings ("NTAG 424 DNA", "NXP NHS3152"), which is
 * the value every consumer of it — `mocks/tagShopMock.ts`, and through it the encode
 * helpers — needs to be a `TagFamily`. It typed as `string`, so the mismatch surfaced only
 * at the assignment (`src/mocks/tagShopMock.ts(45,5): TS2322 … 'string' is not assignable
 * to type 'TagFamily | null | undefined'`) and at runtime as a mocked variant the desk
 * reader refuses with "not supported on the desk reader yet". Typing the record as
 * `TagFamily` is what stops the display-name form coming back.
 *
 * A capability the registry has no family for maps to `other`, which is deliberately NOT
 * encodable — the honest answer for X DNA and the sense chips, which have no reader path
 * (see `TagEncodeStrategy` in packages/types). `other` is not a fallback for "unknown": it
 * is asserted per capability here, because guessing a family breaks encode AND verify
 * (VERA-790).
 *
 * This derivation is legitimate ONLY for the CATALOG/mock surface, where capability and
 * chip are two facts about a product we ourselves defined. The physical encode path takes
 * the family from the authoritative Shopify variant metafield and blocks when it is absent
 * — it never asks this table.
 */
export const CAPABILITY_TO_TAG_FAMILY: Record<TagCapability, TagFamily> = {
  Connect: 'ntag213',
  Verify: 'ntag424_dna',
  // The TT part is a 424 DNA with a tamper loop — same Type-4 SUN encode path, and the
  // registry has no separate member for it.
  'Verify + Tamper': 'ntag424_dna',
  'Verify + Trace': 'em4425',
  'Verify + Vault': 'other', // NTAG X DNA — no reader path yet
  Temperature: 'other', // NXP NHS3152
  Moisture: 'other', // NTAG 22x DNA StatusDetect
  Pressure: 'other', // NTAG 22x DNA StatusDetect
  // Proximity is UHF-only (no product ships against it — documented capability only).
  Proximity: 'other', // Axzon Magnus S3
}

// ─── SKU codes (stable, hyphenless so the SKU splits cleanly on "-") ──────────

export const FORM_CODE: Record<TagFormFactor, string> = {
  Sticker: 'STK', Solid: 'SLD', FabricPatch: 'FPT', FabricLabel: 'FLB',
  Closure: 'CLO', ClosureLoop: 'CLP', Card: 'CRD', Dial: 'DIAL',
}
export const CAP_CODE: Record<TagCapability, string> = {
  Connect: 'CNT', Verify: 'VER', 'Verify + Tamper': 'VERTAMP', 'Verify + Trace': 'VERTRC',
  'Verify + Vault': 'VERVLT', Temperature: 'TEMP', Moisture: 'MOIS', Pressure: 'PRES', Proximity: 'PROX',
}
/**
 * VRG-780 — narrow the loose `form_factor` the shop API returns.
 *
 * `TagShopProduct.form_factor` is typed `string | null` because it arrives from
 * Shopify, where it is a metafield nobody in this codebase writes. Every
 * catalogue function that takes a `TagFormFactor` therefore needs a gate in
 * front of it, and a cast is not one: `'Woven' as TagFormFactor` compiles and
 * then indexes `FORM_CODE` to `undefined`, which comes out as a filename
 * reading `VERA-undefined-…` rather than as an error. (`Woven` is not
 * hypothetical — it is the name the seed used before the form was split into
 * FabricPatch and FabricLabel, and VRG-242 found SKUs still carrying it.)
 *
 * Keyed on `FORM_CODE` rather than a second list of the form names, so a form
 * added to the union cannot be missing here.
 */
export const isTagFormFactor = (value: unknown): value is TagFormFactor =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(FORM_CODE, value)

const CODE_TO_FORM = Object.fromEntries(
  Object.entries(FORM_CODE).map(([f, c]) => [c, f as TagFormFactor]),
) as Record<string, TagFormFactor>
const CODE_TO_CAP = Object.fromEntries(
  Object.entries(CAP_CODE).map(([cap, c]) => [c, cap as TagCapability]),
) as Record<string, TagCapability>

// ─── Pricing constants (mirror the seed script) ──────────────────────────────

export const QUANTITY_TIERS: QuantityTier[] = [100, 250, 500, 1000]
/** @100 = full list (front-loads ship+pack); discounts amortize it as volume grows. */
export const VOLUME_DISCOUNT: Record<QuantityTier, number> = { 100: 0, 250: 0.08, 500: 0.15, 1000: 0.22 }
/** Ferrite premium (~2.5–3× a plain label); does NOT shrink at volume. ⚠️ confirm w/ quote. */
export const ONMETAL_SURCHARGE_PER_UNIT = 0.6
/**
 * Card metal-body premium over the plastic Card base (~Metal $2.80 vs plastic) — same
 * magnitude as the on-metal ferrite surcharge; a per-unit adder (does NOT shrink at
 * volume). TODO(VRG-156) provisional — RFQ pending.
 */
export const CARD_METAL_SURCHARGE_PER_UNIT = 1.6
/** Extra-Durable (PPS/IP68 capsule) premium over Standard. Applies to BOTH fabric forms
 *  — the capsule rides in the carrier either way. ⚠️ confirm w/ quote. */
export const EXTRA_DURABLE_SURCHARGE_PER_UNIT = 0.4
/** Embroidered finish premium (Vera-logo embroidery). TODO(VRG-156) provisional — RFQ pending. */
export const EMBROIDERY_SURCHARGE_PER_UNIT = 0.35

/** FORM_BASE = per-unit LIST at the smallest tier (100); the @100 premium recovers ship+pack. */
const FORM_BASE: Record<TagFormFactor, number> = {
  /* ⚠️ Dial = Sticker DELIBERATELY, AND IT IS NOT AN ESTIMATE. This part was
     sold as a 28mm Sticker until 2026-09-18; splitting it into its own form is
     a catalogue change, not a repricing, so it must cost exactly what it cost
     yesterday. Inventing a number here would have silently changed a live price
     as a side effect of a refactor.
     TODO: the true ferrite-inclusive cost needs the same RFQ as the grades
     below — note that on-metal is currently billed as a per-unit SURCHARGE on
     top of this, which for Dial is now unconditional. */
  Dial: 1.2,
  Sticker: 1.2,
  Closure: 1.65, Solid: 1.75, FabricPatch: 1.8,
  /* ⚠️ = Closure, AND IT IS A PLACEHOLDER, NOT A QUOTE. Closure Loop is not
     manufactured and has no RFQ, so there is no number to state. It is listed
     at the Closure price because this Record is TOTAL over the union and the
     compiler requires an entry — not because anyone has costed a twin-conductor
     cord, which carries more material and an extra termination step than a
     single-arm seal and will almost certainly cost more.

     This does not reach a buyer: the form is in COMING_SOON_FORMS, so the buy
     actions refuse it. Replace with the real figure when the RFQ lands, and do
     not read the equality with Closure as a decision — unlike Dial = Sticker
     (a split, where keeping the price identical was the whole point), nothing
     here was ever sold at this price. */
  ClosureLoop: 1.65, // ⚠️ placeholder — see above; unorderable, no RFQ
  // Label carries more material and an extra assembly step (fold + seam-tail construction,
  // and for the durable grade an encapsulated transponder set into the carrier) than a
  // flat sewn-on patch. TODO provisional — same RFQ as the patch grade.
  FabricLabel: 1.95,
  Card: 3.5, // TODO(VRG-156) provisional — no RFQ yet (plastic ID-1 card + 424-class chip)
}
/** CAP_DELTA over Verify (chip premium). */
const CAP_DELTA: Record<TagCapability, number> = {
  Connect: -0.35, Verify: 0, 'Verify + Tamper': 0.25,
  'Verify + Trace': 0.45, 'Verify + Vault': 4.0, // X DNA — not volume-priced; ⚠️ flag
  // NXP-native phone-tap sense chips (NHS3152 / 22x DNA StatusDetect) cost materially
  // more than base 424 (bare-IC spot ~$2.80–4.09). Delta set so the LIST governs (stays
  // above the landed floor on Sticker + Closure) while reflecting the pricier chip.
  Temperature: 2.5, Moisture: 2.5, Pressure: 2.5, // TODO(VRG-156) provisional — RFQ pending
  Proximity: 0, // UHF; no product ships against it
}

// Min-markup-over-landed floor (tariff auto-guard). ⚠️ CHIP_COST/FORM_CONV are RFQ-pending.
export const MIN_MARKUP = 1.3
export const TARIFF_PCT = 0.45 // conservative China stack (25% §301 + ~20% IEEPA); ⚠️ volatile, broker-check
export const FREIGHT_PER_UNIT = 0.02 // ~1–2.5¢/tag express; not dutiable (19 USC 1401a)
const CHIP_COST: Record<TagCapability, number> = {
  Connect: 0.1, Verify: 0.4, 'Verify + Tamper': 0.5, 'Verify + Trace': 0.3, 'Verify + Vault': 1.5 /* ⚠️RFQ */,
  // NXP sense chips (NHS3152 / 22x DNA StatusDetect) are pricier NFC phone-tap parts —
  // provisional landed basis kept below the CAP_DELTA list so the list still governs.
  // TODO(VRG-156) provisional — RFQ pending (bare-IC spot ~$2.80–4.09).
  Temperature: 1.5, Moisture: 1.5, Pressure: 1.5,
  Proximity: 3.0 /* ⚠️Axzon RFQ */,
}
const FORM_CONV: Record<TagFormFactor, number> = {
  Dial: 0.08, // = Sticker; see the note in FORM_BASE — a split, not a repricing
  Sticker: 0.08, FabricPatch: 0.3, FabricLabel: 0.4, Closure: 0.45, Solid: 0.5,
  ClosureLoop: 0.45, // ⚠️ placeholder = Closure — see the FORM_BASE note; no RFQ, unorderable
  Card: 0.6, // TODO(VRG-156) provisional — card-body assembly (lamination/printing) adder
}

/** Customizable faces per form — gates the Customization add-on's front/back (VRG-142). */
export const SIDES: Record<TagFormFactor, number> = {
  // Label = 2: a folded label has a front and a back face. Patch = 1: sewn flat, one face.
  Sticker: 1, Solid: 2, FabricPatch: 1, FabricLabel: 2, Closure: 1, Card: 2,
  // ClosureLoop = 1: the body is a disc whose back face carries the cord terminations,
  // so there is one face left to print on. Same reasoning as Closure.
  ClosureLoop: 1,
  // Dial = 1: it mounts face-down behind a dial, so only one face is ever seen.
  Dial: 1,
}

const round2 = (n: number) => Math.round(n * 100) / 100

// ─── Pricing functions ───────────────────────────────────────────────────────

/** China landed cost = factory (chip + form conversion) × (1 + tariff) + freight. */
export function landedCost(form: TagFormFactor, capability: TagCapability): number {
  const factory = (CHIP_COST[capability] ?? 0) + (FORM_CONV[form] ?? 0)
  return factory * (1 + TARIFF_PCT) + FREIGHT_PER_UNIT
}

/** Per-unit price @100 = max(hand-set list, landed × MIN_MARKUP) — the floor auto-guards tariff spikes. */
export function unitPrice(form: TagFormFactor, capability: TagCapability): number {
  const list = (FORM_BASE[form] ?? 0) + (CAP_DELTA[capability] ?? 0)
  return round2(Math.max(list, landedCost(form, capability) * MIN_MARKUP))
}

/** Case total for a quantity tier = unit × qty × (1 − volume discount). */
export function casePrice(form: TagFormFactor, capability: TagCapability, qty: QuantityTier): number {
  return round2(unitPrice(form, capability) * qty * (1 - (VOLUME_DISCOUNT[qty] ?? 0)))
}

/** On-metal add-on surcharge for a line = per-unit ferrite premium × quantity (scales with qty). */
export function onMetalSurcharge(qty: number): number {
  return round2(ONMETAL_SURCHARGE_PER_UNIT * qty)
}

/** Card metal-construction surcharge for a line = per-unit metal premium × quantity. */
export function cardMetalSurcharge(qty: number): number {
  return round2(CARD_METAL_SURCHARGE_PER_UNIT * qty)
}

// ─── SKU: build / parse / derive tag_family ──────────────────────────────────

/** Size code = the size with "mm" and non-alphanumerics stripped (matches the seed script). */
export function sizeCode(size: string): string {
  return String(size).replace(/mm/gi, '').replace(/[^A-Za-z0-9]/g, '')
}

/**
 * The three shapes a size token can describe.
 *
 * `Other` exists because two Closure sizes are named rather than measured
 * (`SINGLE`, `DOUBLE` — arm count) and are neither round nor rectangular. Collapsing
 * them into one of the other two would be a false statement on a filter a buyer
 * might one day select; omitting them would make Closure the only form with no
 * shape at all.
 */
export type TagShape = 'Round' | 'Rectangle' | 'Other'

/**
 * Shape, DERIVED from the size token rather than declared per form.
 *
 * ⚠️ THIS REPLACES A HAND-MAINTAINED `FORM_INFO[form].shapes`, AND THE REASON IS
 * THAT THE HAND-MAINTAINED ONE WAS WRONG. Solid and the since-removed Hang both declared
 * `['Round','Rectangle']` while every one of their size tokens was
 * single-dimension — so the shop advertised, and would have let a buyer filter
 * for, a rectangular Solid tag that has no orderable size.
 *
 * The convention was already there to be read: a one-dimension token is a
 * diameter (`30mm`) and a two-dimension token is width × height (`45x25mm`).
 * Deriving it means the list cannot disagree with what is purchasable — add a
 * rectangular size and `Rectangle` appears the same day; there is no second
 * edit to forget.
 */
export function shapeOfSize(size: string): TagShape {
  const s = String(size).trim()
  if (/^[\d.]+mm$/i.test(s)) return 'Round'
  if (/^[\d.]+x[\d.]+mm$/i.test(s)) return 'Rectangle'
  return 'Other'
}

export function buildSku(
  form: TagFormFactor, capability: TagCapability, size: string, qty: QuantityTier | number,
): string {
  return `VERA-${FORM_CODE[form]}-${CAP_CODE[capability]}-${sizeCode(size)}-${qty}`
}

/* ═══════════════════════════════════════════════════════════════════════════
   BUILD OPTIONS — the axes Shopify structurally cannot carry.

   ⚠️ SHOPIFY ALLOWS THREE OPTIONS PER PRODUCT AND ALL THREE ARE SPENT:
   Capability · Size · Quantity. So durability, construction, on-metal, adhesive
   and card material CANNOT be variants — they are cart-level add-ons, which is
   what the seed means by "ADD-ONS, not variants/options/products".

   That is a merchandising constraint, not a manufacturing one. The factory has
   to know whether it is making a Standard or an Extra-Durable patch, ferrite or
   not. Before this, nothing recorded it: there is no column for any of these in
   the schema, and `grep` finds no SKU carrying a modifier anywhere in either
   repo. The "XD SKU suffix" referenced at the top of this file was an intent
   that was never implemented.

   ── ⚠️ TWO SKUs, NOT ONE LONGER ONE ──────────────────────────────────────
   `buildSku` is UNCHANGED and must stay that way. It is the Shopify variant
   key: the store is seeded with it, orders reference it, and `parseSku` is
   specified at exactly five parts. Appending segments to it would invalidate
   every seeded variant.

   `buildBuildSku` is the second projection — what is actually manufactured and
   what a tag was encoded as (`tags.product_sku`). It is the catalog SKU plus
   the selections, so the catalog SKU is a strict PREFIX of it and either can be
   recovered from the other.

   A third projection exists and is deliberately lossy: the picture name (see
   `variantImagery.ts`), which drops capability, quantity and the two modifiers
   that a straight-on photograph cannot show.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ── THE OPTION REGISTRY ───────────────────────────────────────────────────
 *
 * Spencer, 2026-09-18: "MCRD doesn't really make sense if the catalog SKU
 * already has CRD in it ... the options are really determined by what's
 * selected by the catalog SKU already."
 *
 * Right, and it generalises: **the form segment scopes the vocabulary**, so a
 * modifier only has to name the DIFFERENCE, never re-state the form.
 *
 *   MCRD  ->  METAL     the CRD already says Card
 *   MTL   ->  METAL     same word; the forms are mutually exclusive
 *
 * ⚠️ `METAL` DELIBERATELY MEANS TWO THINGS, AND CANNOT BE AMBIGUOUS. On
 * Sticker/Solid/Closure it is the ferrite on-metal build; on Card it is a
 * metal body instead of plastic. Card has no ferrite build and the other four
 * have no body material, so no SKU can carry both senses and no form can read
 * it the wrong way. Resolution is `(form, token)`, never `token` alone — which
 * is why `parseBuildOptions` takes the form.
 *
 * The other consequence of scoping: options a form implies BY DEFINITION are
 * never written. A Sticker is adhesive by construction, so `ADH` appears only
 * on Solid, where it is a real choice. A segment present always means "this
 * differs from the default for THIS form".
 *
 * ⚠️ A REGISTRY, NOT A CHAIN OF `if`s. Everything past the quantity is an open,
 * per-form list — adding an option to one form must not mean editing a builder,
 * a validator and a parser in three places and forgetting the fourth. That is
 * the shape of the bug this file already carries elsewhere (VRG-242).
 */
export type BuildOptionSpec = {
  /** The SKU segment. Names the difference, never the form. */
  code: string
  /** Which forms may carry it. */
  forms: readonly TagFormFactor[]
  /** Human reason, used verbatim in the rejection message. */
  label: string
  /** Options this one forces on (e.g. embroidery is always Extra-Durable). */
  implies?: readonly string[]
  /** Options that must ALSO be present (e.g. Solid ferrite needs adhesive). */
  requires?: readonly string[]
  /** Options it cannot co-exist with. */
  conflicts?: readonly string[]
  /**
   * Whether this option changes a STRAIGHT-ON PHOTOGRAPH of the tag.
   *
   * ⚠️ THIS IS PER (FORM, OPTION), NOT PER TOKEN — which is the whole reason it
   * lives on the spec rather than in a list of token names. `METAL` on Card is
   * a metal body: obviously visible. `METAL` on Sticker/Solid/Closure is
   * a ferrite backing: a thickness change, invisible from above. Same token,
   * opposite answers, resolved by the form.
   *
   * False does NOT mean "invisible in 3-D". A ferrite build and an adhesive
   * back both show in a GLB or an edge-on view; they just cannot be
   * photographed apart from the front. So the 2-D key is a strict SUBSET of the
   * 3-D key, and this flag is the only thing separating them.
   */
  visible2d: boolean
  /**
   * This option collapses the tag to ONE usable face.
   *
   * ⚠️ `SIDES[form]` IS ONLY THE DEFAULT, AND FOR SOLID THE DEFAULT IS THE
   * ADHESIVE-FREE BUILD. catalog.ts: "Add adhesive to stick it flat, which
   * makes it one-sided ... Leave it adhesive-free for a two-sided disc you can
   * hang, sit, or fix yourself." So `SIDES[Solid] = 2` describes one of the two
   * configurations, not the form.
   *
   * This is why `visible2d: false` on ADH is not the whole story. Adhesive does
   * not change the FRONT photograph at all — a 30 mm puck looks the same from
   * above either way — but it changes whether a BACK photograph exists: an
   * adhesive puck's reverse is liner, not a finished face. Modelled as a side
   * count rather than a second front image, because there is no second front
   * image to take.
   */
  oneSided?: boolean
}

const FABRIC_FORMS = ['FabricPatch', 'FabricLabel'] as const
/** Forms where ferrite is a CHOICE the buyer makes. Dial is absent by design. */
const FERRITE_FORMS = ['Sticker', 'Solid', 'Closure'] as const

/**
 * Forms that ARE ferrite-backed by construction, with no option to remove it.
 *
 * ⚠️ NOT AN OPTION, SO IT IS NEVER IN A SKU. Spencer, 2026-09-18:
 * "VERA-DIAL-28-METAL -> VERA-DIAL-28? since metal is implied." Yes — and it is
 * the same rule already applied to adhesive on Sticker: an option a form
 * implies BY DEFINITION is not written, because the form segment already says
 * it. `VERA-DIAL-VER-28-100` is complete.
 *
 * This replaced a `forcedOn` flag that ADDED the segment and rejected its
 * absence. That was the wrong shape: it modelled ferrite as an option that
 * happened to be mandatory, when it is not an option at all.
 *
 * ⚠️ PRICING MUST ASK `formHasFerrite`, NOT "is METAL in the options". The
 * surcharge is a line-level add that deliberately does NOT take the volume
 * discount (see ONMETAL_SURCHARGE_PER_UNIT), so folding it into FORM_BASE would
 * have quietly made a Dial CHEAPER at 250/500/1000. Keeping it a surcharge
 * keeps the price identical to what this part cost as a 28mm on-metal Sticker.
 */
const FERRITE_BY_CONSTRUCTION = new Set<TagFormFactor>(['Dial'])

/**
 * Whether a build is ferrite-backed — inherent to the form, or chosen.
 *
 * Call this rather than testing for the METAL code: on a Dial there is no code
 * to test, and testing for one returns false on a part that is entirely ferrite.
 * CartContext and TagsShopProductPage decide the surcharge from `onMetal`; both
 * must pass through here for a Dial to be billed correctly.
 */
export function formHasFerrite(form: TagFormFactor, codes: readonly string[] = []): boolean {
  return FERRITE_BY_CONSTRUCTION.has(form) || codes.includes('METAL')
}

/**
 * Order here IS the order in the SKU. Grade before construction before
 * material, so a reader scans coarse-to-fine.
 */
export const BUILD_OPTIONS: readonly BuildOptionSpec[] = [
    /* A different weave and ~4x the thickness — reads plainly in a flat shot. */
  {code: 'XD', forms: FABRIC_FORMS, label: 'Extra-Durable grade', visible2d: true},
  {
    code: 'EMBR', forms: ['FabricPatch'], label: 'embroidered construction',
    /* catalog.ts: "embroidered (thicker, always Extra-Durable)". */
    implies: ['XD'],
    /* Raised stitching — the most visible option in the registry. */
    visible2d: true,
  },
  {
    code: 'METAL', forms: FERRITE_FORMS, label: 'ferrite on-metal build',
    /* A backing layer. Thicker edge-on, identical from the front. */
    visible2d: false,
    /* catalog.ts on Solid: adhesive "unlocks the on-metal (ferrite) option".
       A free-hanging disc has no surface to isolate from. `requires` is only
       enforced where the required option is itself legal — see the check. */
    requires: ['ADH'],
  },
    /* A different material entirely, not a backing. */
  {code: 'METAL', forms: ['Card'], label: 'metal body', visible2d: true},
    /* The front face is unchanged; the reverse becomes liner rather than a
     second finished face, so the tag drops to one side. */
  {code: 'ADH', forms: ['Solid'], label: 'adhesive backing', visible2d: false, oneSided: true},
  /**
   * Colourway. Spencer, 2026-09-18: "black or white, and this is only for tags
   * intended to be attached to an object or sewn onto — add it to both fabric
   * forms for now, maybe others in future."
   *
   * ⚠️ BLACK IS THE DEFAULT AND IS THEREFORE NEVER WRITTEN. Every part in the
   * catalogue today is black, so `WHITE` names the difference and a plain
   * `VERA-FPT-25` stays the black woven patch — consistent with every other
   * option here, and it means adding this axis renames nothing.
   *
   * Last in the order: grade and construction are what the part IS, colour is
   * how it is finished. Scanning a name gives construction before appearance.
   *
   * ⚠️ WIDENING THIS IS A ONE-LINE CHANGE WITH A LARGE COST. Adding a form to
   * `forms` DOUBLES that form's shot list, because colour is visible in every
   * frame. Fabric alone takes the library from 52 to 75; all eight forms would
   * take it past 100.
   */
  {code: 'WHITE', forms: FABRIC_FORMS, label: 'white colourway', visible2d: true},
] as const

/**
 * Usable faces for a build — `SIDES[form]` adjusted by its options.
 *
 * Call this rather than reading `SIDES` directly anywhere a build's options are
 * known. `SIDES` alone answers for the DEFAULT configuration only, which for
 * Solid is the adhesive-free one.
 */
export function sidesFor(form: TagFormFactor, codes: readonly string[] = []): number {
  const normalized = normalizeBuildOptions(form, codes)
  for (const code of normalized) {
    if (buildOptionFor(form, code)?.oneSided) return 1
  }
  return SIDES[form] ?? 1
}

/** The options that change a flat photo of this form — the 2-D imagery key. */
export function visible2dOptionsFor(form: TagFormFactor): BuildOptionSpec[] {
  return buildOptionsFor(form).filter((o) => o.visible2d)
}

/** The spec for a token ON A GIVEN FORM. Null when that form cannot carry it. */
export function buildOptionFor(
  form: TagFormFactor, code: string,
): BuildOptionSpec | null {
  return BUILD_OPTIONS.find(
    (o) => o.code === code && (o.forms as readonly string[]).includes(form),
  ) ?? null
}

/** Every option a form can carry, in SKU order. Powers the configurator UI. */
export function buildOptionsFor(form: TagFormFactor): BuildOptionSpec[] {
  return BUILD_OPTIONS.filter((o) => (o.forms as readonly string[]).includes(form))
}

/**
 * Why this option set is not orderable for this form, or null if it is.
 *
 * ⚠️ RETURNS A REASON, NOT A BOOLEAN. Every one of these is a factory
 * instruction; "invalid" is not actionable to whoever has to fix the order, and
 * a silently-dropped option ships the wrong physical part.
 */
export function invalidBuildOption(
  form: TagFormFactor, codes: readonly string[] = [],
): string | null {
  const set = new Set(codes)
  for (const code of set) {
    const spec = buildOptionFor(form, code)
    if (!spec) return `${form} has no "${code}" option`
    for (const r of spec.requires ?? []) {
      /* Only binding when the required option is legal on this form: Sticker
         ferrite must NOT demand ADH, which Sticker cannot carry at all. */
      if (buildOptionFor(form, r) && !set.has(r))
        return `${spec.label} on ${form} requires "${r}"`
    }
    for (const c of spec.conflicts ?? []) {
      if (set.has(c)) return `"${code}" and "${c}" cannot be combined`
    }
  }
  return null
}

/** Applies `implies`, then sorts into registry order. */
export function normalizeBuildOptions(
  form: TagFormFactor, codes: readonly string[] = [],
): string[] {
  const set = new Set(codes)
  for (const code of [...set]) {
    for (const imp of buildOptionFor(form, code)?.implies ?? []) set.add(imp)
  }
  const order = buildOptionsFor(form).map((o) => o.code)
  return [...set].sort((a, b) => order.indexOf(a) - order.indexOf(b))
}

/**
 * The manufacturing / encoding SKU: the catalog SKU plus the build options.
 *
 * ⚠️ TWO SKUs, NOT ONE LONGER ONE. `buildSku` is the Shopify VARIANT key — the
 * store is seeded with it and orders reference it — and Shopify allows only
 * three options (Capability · Size · Quantity), all spent. These options cannot
 * be variants, so they live here instead. The catalog SKU is a strict PREFIX of
 * the build SKU, so either recovers the other.
 *
 * Throws rather than emitting an unorderable SKU: it is cheaper to fail here
 * than at goods-in.
 */
export function buildBuildSku(
  form: TagFormFactor, capability: TagCapability, size: string,
  qty: QuantityTier | number, codes: readonly string[] = [],
): string {
  const normalized = normalizeBuildOptions(form, codes)
  const bad = invalidBuildOption(form, normalized)
  if (bad) throw new Error(`buildBuildSku(${form}): ${bad}`)
  return [buildSku(form, capability, size, qty), ...normalized].join('-')
}

/** Resolve a build SKU's trailing segments against its own form. */
export function parseBuildOptions(sku: string): BuildOptionSpec[] | null {
  const parsed = parseSku(sku)
  if (!parsed?.form) return null
  const out: BuildOptionSpec[] = []
  for (const code of parsed.modifiers) {
    const spec = buildOptionFor(parsed.form, code)
    if (!spec) return null
    out.push(spec)
  }
  return out
}

export type ParsedSku = {
  form: TagFormFactor | null
  capability: TagCapability | null
  sizeCode: string
  qty: number | null
  /** Build-SKU modifier segments (`['XD','EMBR']`). Empty for a catalog SKU. */
  modifiers: string[]
}

/** Parse `VERA-<FORM>-<CAP>-<SIZE>-<QTY>` (5 hyphen-split parts). Unknown codes → null. */
export function parseSku(sku: string): ParsedSku | null {
  const parts = String(sku).split('-')
  /* ⚠️ `>= 5`, NOT `=== 5`. A build SKU is the catalog SKU plus modifier
     segments, so the catalog SKU is a strict prefix of it. Pinned at exactly
     five, this returned null for every build SKU — and callers treat null as
     "unrecognised", which would have made an encoded tag unparseable the first
     time anyone ordered an Extra-Durable patch. The modifiers are returned in
     `modifiers` so nothing is silently discarded either. */
  if (parts.length < 5 || parts[0] !== 'VERA') return null
  const [, formC, capC, size, qtyStr, ...modifiers] = parts
  const qty = Number(qtyStr)
  return {
    form: CODE_TO_FORM[formC] ?? null,
    capability: CODE_TO_CAP[capC] ?? null,
    sizeCode: size,
    qty: Number.isFinite(qty) ? qty : null,
    modifiers,
  }
}

export function capabilityToTagFamily(capability: TagCapability): TagFamily {
  return CAPABILITY_TO_TAG_FAMILY[capability]
}

/** Derive the chip family from a catalog SKU (via its capability segment). Null if unrecognized. */
export function skuToTagFamily(sku: string): TagFamily | null {
  const cap = parseSku(sku)?.capability
  return cap ? CAPABILITY_TO_TAG_FAMILY[cap] : null
}
